import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { 
  Key, 
  Eye, 
  EyeOff, 
  Check, 
  X, 
  Loader2, 
  Shield, 
  AlertTriangle,
  RefreshCw,
  Trash2,
  Save
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function APICredentialsPage() {
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProvider, setEditingProvider] = useState(null);
  const [newKeyValue, setNewKeyValue] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [testing, setTesting] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const getToken = () => localStorage.getItem('carbiq_admin_token');

  const fetchCredentials = async () => {
    try {
      const response = await fetch(`${API_URL}/api/v1/admin/settings/credentials`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch credentials');
      
      const data = await response.json();
      setCredentials(data.credentials || []);
    } catch (error) {
      toast.error('Failed to load credentials');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCredentials();
  }, []);

  const handleSaveCredential = async () => {
    if (!newKeyValue.trim()) {
      toast.error('Please enter an API key');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/v1/admin/settings/credentials`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          provider: editingProvider,
          apiKey: newKeyValue
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save credential');
      }

      toast.success('API key saved successfully');
      setEditingProvider(null);
      setNewKeyValue('');
      setShowKey(false);
      fetchCredentials();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleTestCredential = async (provider, apiKey = null) => {
    setTesting(provider);
    
    try {
      // If no apiKey provided, we're testing the saved one
      // For saved keys, we need to get it from the form or use a flag
      const keyToTest = apiKey || newKeyValue;
      
      if (!keyToTest && !credentials.find(c => c.provider === provider)?.configured) {
        toast.error('No API key to test');
        setTesting(null);
        return;
      }

      const response = await fetch(`${API_URL}/api/v1/admin/settings/credentials/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          provider,
          apiKey: keyToTest || 'use_saved' // Server will use saved if this is 'use_saved'
        })
      });

      const result = await response.json();
      
      if (result.valid) {
        toast.success(result.message);
      } else {
        toast.error(result.message);
      }
      
      fetchCredentials(); // Refresh to get updated test status
    } catch (error) {
      toast.error('Test failed: ' + error.message);
    } finally {
      setTesting(null);
    }
  };

  const handleDeleteCredential = async (provider) => {
    try {
      const response = await fetch(`${API_URL}/api/v1/admin/settings/credentials/${provider}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getToken()}` }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete credential');
      }

      toast.success('API key removed');
      setDeleteConfirm(null);
      fetchCredentials();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const getStatusBadge = (cred) => {
    if (!cred.configured) {
      return <Badge variant="outline" className="text-gray-500">Not Configured</Badge>;
    }
    if (cred.lastTestStatus === 'success') {
      return <Badge className="bg-green-100 text-green-700">Verified</Badge>;
    }
    if (cred.lastTestStatus === 'failed') {
      return <Badge variant="destructive">Invalid</Badge>;
    }
    return <Badge variant="outline" className="text-amber-600 border-amber-600">Untested</Badge>;
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="api-credentials-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Key className="h-6 w-6 text-primary" />
          API Credentials
        </h1>
        <p className="text-muted-foreground">
          Configure API keys for AI features and integrations
        </p>
      </div>

      {/* Status Overview */}
      <Card className="bg-gradient-to-r from-slate-50 to-slate-100 border-slate-200">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <Shield className="h-10 w-10 text-slate-600" />
            <div>
              <h3 className="font-semibold">Configuration Status</h3>
              <p className="text-sm text-muted-foreground">
                {credentials.filter(c => c.configured && c.required).length} of {credentials.filter(c => c.required).length} required credentials configured
              </p>
              {credentials.some(c => c.required && !c.configured) && (
                <div className="flex items-center gap-1 text-amber-600 text-sm mt-1">
                  <AlertTriangle className="h-4 w-4" />
                  <span>Some required API keys are missing</span>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Credentials List */}
      <div className="grid gap-4">
        {credentials.map((cred) => (
          <Card key={cred.provider} data-testid={`credential-${cred.provider}`}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    {cred.name}
                    {cred.required && (
                      <Badge variant="outline" className="text-xs">Required</Badge>
                    )}
                  </CardTitle>
                  <CardDescription>{cred.description}</CardDescription>
                </div>
                {getStatusBadge(cred)}
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  {cred.configured ? (
                    <>
                      <p className="text-sm font-mono bg-slate-100 px-3 py-1.5 rounded">
                        {cred.maskedKey}
                      </p>
                      {cred.lastUpdated && (
                        <p className="text-xs text-muted-foreground">
                          Last updated: {new Date(cred.lastUpdated).toLocaleDateString()} by {cred.updatedBy}
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">
                      No API key configured
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  {cred.configured && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleTestCredential(cred.provider, null)}
                        disabled={testing === cred.provider}
                        data-testid={`test-${cred.provider}`}
                      >
                        {testing === cred.provider ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <RefreshCw className="h-4 w-4" />
                        )}
                        <span className="ml-1">Test</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => setDeleteConfirm(cred.provider)}
                        data-testid={`delete-${cred.provider}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                  <Button
                    size="sm"
                    onClick={() => {
                      setEditingProvider(cred.provider);
                      setNewKeyValue('');
                      setShowKey(false);
                    }}
                    data-testid={`configure-${cred.provider}`}
                  >
                    <Key className="h-4 w-4 mr-1" />
                    {cred.configured ? 'Update' : 'Configure'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editingProvider} onOpenChange={(open) => !open && setEditingProvider(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {credentials.find(c => c.provider === editingProvider)?.configured ? 'Update' : 'Configure'} {credentials.find(c => c.provider === editingProvider)?.name} API Key
            </DialogTitle>
            <DialogDescription>
              Enter your API key below. Keys are encrypted before storage.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="apiKey">API Key</Label>
              <div className="relative">
                <Input
                  id="apiKey"
                  type={showKey ? 'text' : 'password'}
                  placeholder="Enter your API key..."
                  value={newKeyValue}
                  onChange={(e) => setNewKeyValue(e.target.value)}
                  className="pr-10 font-mono"
                  data-testid="api-key-input"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                  onClick={() => setShowKey(!showKey)}
                >
                  {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            
            {/* Provider-specific instructions */}
            {editingProvider === 'anthropic' && (
              <div className="text-sm text-muted-foreground bg-slate-50 p-3 rounded">
                <p className="font-medium mb-1">Get your Anthropic API key:</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Go to <a href="https://console.anthropic.com/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">console.anthropic.com</a></li>
                  <li>Sign in or create an account</li>
                  <li>Navigate to API Keys section</li>
                  <li>Create a new key and copy it</li>
                </ol>
              </div>
            )}
            
            {editingProvider === 'sendgrid' && (
              <div className="text-sm text-muted-foreground bg-slate-50 p-3 rounded">
                <p className="font-medium mb-1">Get your SendGrid API key:</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Go to <a href="https://app.sendgrid.com/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">app.sendgrid.com</a></li>
                  <li>Navigate to Settings → API Keys</li>
                  <li>Create a new API key with appropriate permissions</li>
                </ol>
              </div>
            )}
          </div>
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => handleTestCredential(editingProvider, newKeyValue)}
              disabled={!newKeyValue || testing === editingProvider}
            >
              {testing === editingProvider ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-1" />
              )}
              Test Key
            </Button>
            <Button
              onClick={handleSaveCredential}
              disabled={!newKeyValue || saving}
              data-testid="save-credential-btn"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <Save className="h-4 w-4 mr-1" />
              )}
              Save Key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove API Key?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the {credentials.find(c => c.provider === deleteConfirm)?.name} API key. 
              Features that depend on this key will stop working.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => handleDeleteCredential(deleteConfirm)}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
